import {
  CardSummary,
  GetMerchantSettingsResponse_MerchantSettings,
} from "projects/frontend-client/export";
import { useFormContext } from "react-hook-form";

import { PaymentOption } from "checkout-experience/components/payment-method/payment-option-provider";
import { CheckoutSession } from "checkout-query-client/hooks/use-checkout-session";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";
import {
  CardExpirationType,
  getExpirationMonthYear,
  isCardFormFieldValueDifferent,
} from "checkout-query-client/utils/helpers";

import { PlainMessage } from "frontend-client";

interface Params {
  editSavedPaymentMethod: boolean | undefined;
  paymentOption: PaymentOption | undefined;
  merchantSettings:
    | PlainMessage<GetMerchantSettingsResponse_MerchantSettings>
    | undefined;
  checkoutSession: CheckoutSession | undefined;
}

export const useSecurityCodeFieldBySettings = ({
  editSavedPaymentMethod = false,
  paymentOption,
  merchantSettings,
  checkoutSession,
}: Params) => {
  const { formId = "", paymentType = PaymentType.creditCard } =
    paymentOption ?? {};
  const { paymentMethods: savedPaymentMethods } = checkoutSession ?? {};
  const isSavedPaymentMethod = formId !== paymentType?.toString();
  const { watch } = useFormContext();
  let securityCodeForPay = true; // Always Required case
  const securityCodeOnPayWithProfile =
    merchantSettings?.paymentMethods?.cardPaymentMethod
      ?.captureSecurityCodeOnPayWithProfile;

  const securityCodeOnCardUpdate =
    merchantSettings?.paymentMethods?.cardPaymentMethod
      ?.captureSecurityCodeOnCardUpdate;

  const securityCodeRequired =
    merchantSettings?.paymentMethods?.cardPaymentMethod?.securityCodeRequired;

  if (
    // Never required case
    securityCodeOnPayWithProfile === false &&
    securityCodeOnCardUpdate === false &&
    securityCodeRequired === false
  ) {
    securityCodeForPay = false;
  } else if (
    //Required when making card update or new card case
    securityCodeOnPayWithProfile === false &&
    securityCodeOnCardUpdate === true &&
    isSavedPaymentMethod
  ) {
    const formFields = watch(`${paymentOption?.formId}`) as ChexFieldValues;
    const paymentMethod = savedPaymentMethods?.find(
      (p) => p.paymentMethodId === paymentOption?.formId
    );
    const card =
      paymentMethod?.paymentMethod.case === "card"
        ? (paymentMethod?.paymentMethod.value as CardSummary)
        : undefined;
    if (card) {
      let formFieldExpiration: CardExpirationType = {
        expirationMonth: card.expirationMonth,
        expirationYear: card.expirationYear,
      };
      if (formFields?.expiration) {
        formFieldExpiration = getExpirationMonthYear(formFields?.expiration);
      } else if (formFields?.expirationMonth && formFields?.expirationYear) {
        formFieldExpiration = {
          expirationMonth: Number(formFields?.expirationMonth),
          expirationYear: Number(formFields?.expirationYear),
        };
      }
      securityCodeForPay =
        isCardFormFieldValueDifferent(card, formFieldExpiration) &&
        editSavedPaymentMethod;
    }
  }
  return securityCodeForPay;
};
